import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/dateHelpers';
import { useAuth } from '../components/auth/AuthContext';
import { collectionPath } from '../utils/firestoreCollections';
import { usePermissions } from '../hooks/usePermissions';
import { FEATURE_PERMISSIONS, getRbacMatchNames } from '../utils/permissions';
import PaginationControls from '../components/common/PaginationControls';
import Tooltip from '../components/common/Tooltip';
import Toast from '../components/common/Toast';
import { search as typesenseSearch, isTypesenseAvailable } from '../utils/typesense';
import { useTableState } from '../hooks/useTableState';
import { useFilterBar } from '../hooks/useFilterBar';
import FilterActions from '../components/common/FilterActions';

const PAGE_SIZE = 25;

const ContactsPage = () => {
    const { currentUser } = useAuth();
    const { can } = usePermissions(currentUser);
    const navigate = useNavigate();

    const [contacts, setContacts] = useState([]); // Fallback Firestore contacts
    const [loading, setLoading] = useState(true);

    const fb = useFilterBar({
        q: '',
        rep: '',
        focus: '',
        primary: 'all',
    });

    const { sortBy, sortDir, page: currentPage, setSort: setTableSort, setPage: setCurrentPage } = useTableState({ sortBy: 'customerName', sortDir: 'asc', page: 1 });
    const [toast, setToast] = useState(null);
    const [typesenseChecking, setTypesenseChecking] = useState(true);
    const [typesenseEnabled, setTypesenseEnabled] = useState(false);
    const [typesenseResults, setTypesenseResults] = useState([]);
    const [facets, setFacets] = useState({ salesRepNames: [], contactFocus: [] });
    const [typesenseTotalFound, setTypesenseTotalFound] = useState(0);
    const [contactFocusCollectionOptions, setContactFocusCollectionOptions] = useState(['Not Specified', 'Golf', 'Tennis', 'Pickleball', 'Other']);

    // Load focus options from contactFocus collection (source of truth, unaffected by Typesense staleness)
    useEffect(() => {
        getDocs(collection(db, collectionPath('contactFocus')))
            .then(snap => {
                const names = snap.docs.map(d => d.data().name).filter(Boolean);
                setContactFocusCollectionOptions(prev => {
                    const merged = [...new Set([...prev, ...names])].sort();
                    return merged;
                });
            })
            .catch(err => console.warn('[ContactsPage] Could not load contactFocus options:', err));
    }, []);

    // Check Typesense availability
    useEffect(() => {
        const checkTypesense = async () => {
            const available = await isTypesenseAvailable();
            setTypesenseEnabled(available);
            setTypesenseChecking(false);
            if (available) {
                console.log('[ContactsPage] Typesense available for search');
            }
        };
        checkTypesense();
    }, []);

    // Typesense search with server-side sorting, pagination, and faceted filtering
    useEffect(() => {
        if (!typesenseEnabled) {
            setTypesenseResults([]);
            return;
        }

        let cancelled = false;

        const performSearch = async () => {
            setLoading(true);
            try {
                const startTime = performance.now();
                
                // Build filter_by query
                const filters = [];
                if (fb.applied.rep) {
                    filters.push(`salesRepNames:=[${fb.applied.rep}]`);
                }
                if (fb.applied.focus) {
                    if (fb.applied.focus === '__EMPTY__') {
                        // Match contacts with no contactFocus set. Relies on the
                        // `hasContactFocus` boolean field populated by syncTypesense.js.
                        filters.push('hasContactFocus:=false');
                    } else {
                        filters.push(`contactFocus:=[${fb.applied.focus}]`);
                    }
                }
                if (fb.applied.primary === 'primary') {
                    filters.push('isPrimary:=true');
                } else if (fb.applied.primary === 'non-primary') {
                    filters.push('isPrimary:=false');
                }
                
                // Map React sort fields to Typesense field names
                // string[] fields (contactFocus, salesRepNames) can't be sorted server-side
                const sortFieldMap = {
                    fullName: 'lastName',
                    customerName: 'customerName',
                    email: 'email',
                    phone: 'phone',
                    contactPosition: 'title',
                    isPrimary: 'isPrimary',
                };
                const tsSortField = sortFieldMap[sortBy] || null;
                const tsSortDir = sortDir === 'asc' ? 'asc' : 'desc';

                const searchParams = {
                    q: fb.applied.q.trim() || '*',
                    query_by: 'firstName,lastName,email,phone,customerName,customerAccountNumber,salesRepNames,title,contactFocus',
                    per_page: PAGE_SIZE,
                    page: currentPage,
                    facet_by: 'salesRepNames,contactFocus,isPrimary',
                    max_facet_values: 100
                };

                if (tsSortField) {
                    searchParams.sort_by = `${tsSortField}:${tsSortDir}`;
                }
                
                if (filters.length > 0) {
                    searchParams.filter_by = filters.join(' && ');
                }
                
                // Apply RBAC for non-admins
                if (!can(FEATURE_PERMISSIONS.CONTACT_VIEW_ALL) && currentUser?.displayName) {
                    const rbacNames = [];
                    if (currentUser?.displayName) rbacNames.push(currentUser.displayName.trim());
                    if (currentUser?.full_name && currentUser.full_name.trim().toLowerCase() !== (currentUser.displayName || '').trim().toLowerCase()) {
                        rbacNames.push(currentUser.full_name.trim());
                    }
                    const rbacFilter = `salesRepNames:=[${rbacNames.join(',')}]`;
                    searchParams.filter_by = searchParams.filter_by 
                        ? `${searchParams.filter_by} && ${rbacFilter}`
                        : rbacFilter;
                }

                const response = await typesenseSearch('contacts', searchParams);
                if (cancelled) return;

                const results = response.hits.map(hit => ({
                    id: hit.document.id,
                    ...hit.document,
                    contactIndex: hit.document.contactIndex ?? 0,
                    contactPosition: hit.document.title || hit.document.contactPosition || '',
                    salesRepNames: hit.document.salesRepNames || [],
                    contactFocus: hit.document.contactFocus || [],
                    isPrimary: hit.document.isPrimary === true || hit.document.isPrimary === 1
                }));

                const totalFound = response.found || 0;
                
                // Extract facets for filter dropdowns
                const extractedFacets = {
                    salesRepNames: [],
                    contactFocus: []
                };
                
                if (response.facet_counts) {
                    response.facet_counts.forEach(facet => {
                        if (facet.field_name === 'salesRepNames') {
                            extractedFacets.salesRepNames = facet.counts.map(c => c.value).sort();
                        } else if (facet.field_name === 'contactFocus') {
                            extractedFacets.contactFocus = facet.counts.map(c => c.value).sort();
                        }
                    });
                }
                
                setFacets(extractedFacets);

                // Enrich contacts missing salesRepNames (only for current page — max 25 contacts)
                let enrichedResults = results;
                const needsEnrichment = results.filter(c => (!c.salesRepNames || c.salesRepNames.length === 0) && c.customerId);
                if (needsEnrichment.length > 0) {
                    try {
                        const uniqueCustomerIds = [...new Set(needsEnrichment.map(c => c.customerId))];
                        const customerMap = new Map();
                        for (let i = 0; i < uniqueCustomerIds.length; i += 10) {
                            const batchIds = uniqueCustomerIds.slice(i, i + 10);
                            const custSnap = await getDocs(query(
                                collection(db, collectionPath('customers')),
                                where('customerId', 'in', batchIds)
                            ));
                            custSnap.docs.forEach(doc => {
                                const d = doc.data();
                                if (d.customerId) customerMap.set(d.customerId, d);
                            });
                        }
                        if (cancelled) return;
                        enrichedResults = results.map(contact => {
                            if ((!contact.salesRepNames || contact.salesRepNames.length === 0) && contact.customerId) {
                                const customer = customerMap.get(contact.customerId);
                                if (customer?.salesReps && Array.isArray(customer.salesReps)) {
                                    const names = customer.salesReps.map(rep => rep.salesRepName || rep.displayName).filter(Boolean);
                                    if (names.length > 0) return { ...contact, salesRepNames: names };
                                } else if (Array.isArray(customer?.salesRepNames) && customer.salesRepNames.length > 0) {
                                    return { ...contact, salesRepNames: customer.salesRepNames };
                                }
                            }
                            return contact;
                        });
                        console.log(`[ContactsPage] Enriched ${needsEnrichment.length} contacts with salesRepNames from customers`);
                    } catch (enrichErr) {
                        console.warn('[ContactsPage] salesRepNames enrichment failed:', enrichErr);
                    }
                }

                const duration = performance.now() - startTime;
                console.log(`[ContactsPage] 🚀 Typesense found ${totalFound} contacts (page ${currentPage}, ${results.length} shown) in ${Math.round(duration)}ms`);
                if (!cancelled) {
                    setTypesenseResults(enrichedResults);
                    setTypesenseTotalFound(totalFound);
                }
            } catch (error) {
                console.error('[ContactsPage] Typesense search error:', error);
                if (!cancelled) {
                    setTypesenseResults([]);
                    setTypesenseTotalFound(0);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        performSearch();
        return () => { cancelled = true; };
    }, [fb.applied.q, fb.applied.rep, fb.applied.focus, fb.applied.primary, typesenseEnabled, currentUser, can, sortBy, sortDir, currentPage, contactFocusCollectionOptions]);

    // Fetch contacts from Firestore (fallback when Typesense unavailable)
    useEffect(() => {
        if (!currentUser || typesenseChecking || typesenseEnabled) {
            return;
        }

        let cancelled = false;

        const fetchContacts = async () => {
            try {
                setLoading(true);
                console.log('[ContactsPage] Typesense unavailable, using Firestore fallback');
                
                const userDisplayName = currentUser.displayName?.toLowerCase();
                
                // Validate displayName for non-admin users
                if (!can(FEATURE_PERMISSIONS.CONTACT_VIEW_ALL) && !userDisplayName) {
                    console.warn('[ContactsPage] Non-admin user has no displayName, showing empty results');
                    if (!cancelled) { setContacts([]); setLoading(false); }
                    return;
                }

                // Fetch customers first for sales rep enrichment
                const customersQuery = query(
                    collection(db, collectionPath('customers')),
                    limit(5000)
                );
                const customersSnap = await getDocs(customersQuery);
                if (cancelled) return;
                const allCustomers = customersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                // Create customer lookup map for quick access
                const customerMap = new Map(allCustomers.map(c => [c.customerId, c]));

                if (can(FEATURE_PERMISSIONS.CONTACT_VIEW_ALL)) {
                    // Admin: fetch all contacts
                    const contactsQuery = query(
                        collection(db, collectionPath('contacts')),
                        limit(5000)
                    );
                    
                    const contactsSnap = await getDocs(contactsQuery);
                    if (cancelled) return;
                    const allContacts = contactsSnap.docs.map(doc => {
                        const contactData = doc.data();
                        const customer = customerMap.get(contactData.customerId);
                        
                        return {
                            id: doc.id,
                            ...contactData,
                            contactIndex: contactData.contactIndex ?? 0,
                            salesRepNames: customer?.salesReps?.map(rep => rep.salesRepName || rep.displayName) || [],
                            isPrimary: contactData.isPrimary === true || contactData.isPrimary === 1
                        };
                    });
                    
                    setContacts(allContacts);
                } else {
                    // Non-admin: fetch assigned customers' contacts
                    const matchNames = getRbacMatchNames(currentUser);
                    const matchSet = new Set(matchNames);
                    const assignedCustomers = allCustomers.filter(customer =>
                        customer.salesReps?.some(rep =>
                            matchSet.has(rep.salesRepName?.toLowerCase().trim())
                        )
                    );
                    
                    // Filter out undefined/null customerId values to prevent Firestore 'in' query errors
                    const assignedCustomerIds = assignedCustomers
                        .map(c => c.customerId)
                        .filter(id => id != null);
                    
                    if (assignedCustomerIds.length === 0) {
                        if (!cancelled) { setContacts([]); setLoading(false); }
                        return;
                    }
                    
                    // Fetch contacts in batches (Firestore 'in' limit is 10)
                    const contactsData = [];
                    for (let i = 0; i < assignedCustomerIds.length; i += 10) {
                        if (cancelled) return;
                        const batchIds = assignedCustomerIds.slice(i, i + 10);
                        const contactsQuery = query(
                            collection(db, collectionPath('contacts')),
                            where('customerId', 'in', batchIds)
                        );
                        
                        const contactsSnap = await getDocs(contactsQuery);
                        contactsSnap.docs.forEach(doc => {
                            const contactData = doc.data();
                            const customer = customerMap.get(contactData.customerId);
                            
                            contactsData.push({
                                id: doc.id,
                                ...contactData,
                                contactIndex: contactData.contactIndex ?? 0,
                                salesRepNames: customer?.salesReps?.map(rep => rep.salesRepName || rep.displayName) || [],
                                isPrimary: contactData.isPrimary === true || contactData.isPrimary === 1
                            });
                        });
                    }
                    
                    if (!cancelled) setContacts(contactsData);
                }
                
                if (!cancelled) setLoading(false);
            } catch (err) {
                console.error('Error fetching contacts:', err);
                if (!cancelled) {
                    setToast({ message: `Error loading contacts: ${err.message}`, type: 'error' });
                    setLoading(false);
                }
            }
        };

        fetchContacts();
        return () => { cancelled = true; };
    }, [currentUser, can, typesenseChecking, typesenseEnabled]);

    // Use Typesense results if available, otherwise Firestore with client-side filtering
    const displayContacts = useMemo(() => {
        // Use Typesense results (already filtered)
        if (typesenseEnabled) {
            return typesenseResults;
        }

        // Fallback to Firestore with client-side filtering
        let filtered = contacts;
        
        // Search filter
        if (fb.applied.q) {
            const searchLower = fb.applied.q.toLowerCase();
            filtered = filtered.filter(contact => {
                const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.toLowerCase();
                const focusStr = Array.isArray(contact.contactFocus) 
                    ? contact.contactFocus.join(', ').toLowerCase() 
                    : (contact.contactFocus || '').toLowerCase();
                const salesRepsStr = Array.isArray(contact.salesRepNames)
                    ? contact.salesRepNames.join(', ').toLowerCase()
                    : '';

                return (
                    fullName.includes(searchLower) ||
                    (contact.email || '').toLowerCase().includes(searchLower) ||
                    (contact.phone || '').toLowerCase().includes(searchLower) ||
                    (contact.contactPosition || '').toLowerCase().includes(searchLower) ||
                    (contact.customerName || '').toLowerCase().includes(searchLower) ||
                    (contact.customerAccountNumber || '').toString().toLowerCase().includes(searchLower) ||
                    focusStr.includes(searchLower) ||
                    salesRepsStr.includes(searchLower)
                );
            });
        }
        
        // Sales rep filter
        if (fb.applied.rep) {
            filtered = filtered.filter(contact => 
                contact.salesRepNames?.includes(fb.applied.rep)
            );
        }
        
        // Focus filter
        if (fb.applied.focus) {
            filtered = filtered.filter(contact => {
                // Sentinel: match contacts with no focus set
                if (fb.applied.focus === '__EMPTY__') {
                    if (contact.contactFocus === undefined || contact.contactFocus === null || contact.contactFocus === '') return true;
                    if (Array.isArray(contact.contactFocus) && contact.contactFocus.length === 0) return true;
                    return false;
                }
                if (Array.isArray(contact.contactFocus)) {
                    return contact.contactFocus.includes(fb.applied.focus);
                }
                return contact.contactFocus === fb.applied.focus;
            });
        }
        
        // Primary filter
        if (fb.applied.primary === 'primary') {
            filtered = filtered.filter(contact => contact.isPrimary === true);
        } else if (fb.applied.primary === 'non-primary') {
            filtered = filtered.filter(contact => !contact.isPrimary);
        }
        
        return filtered;
    }, [typesenseEnabled, typesenseResults, contacts, fb.applied.q, fb.applied.rep, fb.applied.focus, fb.applied.primary]);

    // Sort contacts (Typesense handles server-side for scalar fields; client sorts for string[] fields or Firestore fallback)
    const sortedContacts = useMemo(() => {
        // For Typesense with server-sortable fields, data is already sorted
        const serverSortableFields = ['fullName', 'customerName', 'email', 'phone', 'contactPosition', 'isPrimary'];
        if (typesenseEnabled && serverSortableFields.includes(sortBy)) return displayContacts;

        return [...displayContacts].sort((a, b) => {
            let aVal, bVal;

            switch (sortBy) {
                case 'fullName':
                    aVal = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
                    bVal = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
                    break;
                case 'customerName':
                    aVal = (a.customerName || '').toLowerCase();
                    bVal = (b.customerName || '').toLowerCase();
                    break;
                case 'email':
                    aVal = (a.email || '').toLowerCase();
                    bVal = (b.email || '').toLowerCase();
                    break;
                case 'phone':
                    aVal = (a.phone || '').toLowerCase();
                    bVal = (b.phone || '').toLowerCase();
                    break;
                case 'contactPosition':
                    aVal = (a.contactPosition || '').toLowerCase();
                    bVal = (b.contactPosition || '').toLowerCase();
                    break;
                case 'contactFocus':
                    aVal = (Array.isArray(a.contactFocus) ? a.contactFocus.join(', ') : (a.contactFocus || '')).toLowerCase();
                    bVal = (Array.isArray(b.contactFocus) ? b.contactFocus.join(', ') : (b.contactFocus || '')).toLowerCase();
                    break;
                case 'isPrimary':
                    aVal = a.isPrimary ? 1 : 0;
                    bVal = b.isPrimary ? 1 : 0;
                    break;
                case 'salesReps':
                    aVal = (a.salesRepNames?.[0] || '').toLowerCase();
                    bVal = (b.salesRepNames?.[0] || '').toLowerCase();
                    break;
                default:
                    aVal = a[sortBy] || '';
                    bVal = b[sortBy] || '';
            }

            if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [displayContacts, sortBy, sortDir, typesenseEnabled]);

    // Paginate contacts (Typesense already paginates server-side)
    const paginatedContacts = useMemo(() => {
        if (typesenseEnabled) return sortedContacts;
        const start = (currentPage - 1) * PAGE_SIZE;
        return sortedContacts.slice(start, start + PAGE_SIZE);
    }, [sortedContacts, currentPage, typesenseEnabled]);

    const totalPages = typesenseEnabled 
        ? Math.ceil(typesenseTotalFound / PAGE_SIZE)
        : Math.ceil(sortedContacts.length / PAGE_SIZE);

    // Get unique sales reps and focus options for filters
    const uniqueSalesReps = useMemo(() => {
        // Use Typesense facets if available (more efficient)
        if (typesenseEnabled && facets.salesRepNames.length > 0) {
            return facets.salesRepNames;
        }
        
        // Fallback to extracting from contacts
        const reps = new Set();
        contacts.forEach(contact => {
            contact.salesRepNames?.forEach(rep => reps.add(rep));
        });
        return Array.from(reps).sort();
    }, [typesenseEnabled, facets.salesRepNames, contacts]);
    
    const uniqueFocusOptions = useMemo(() => {
        // Always use contactFocus collection as the source of truth for the dropdown.
        // Typesense facets only reflect indexed (potentially stale) data, so we don't
        // rely on them here — recently added/updated focus values would be missing.
        const options = new Set(contactFocusCollectionOptions);

        // Also include any values seen in the current result set so nothing is lost
        // even if the contactFocus collection hasn't been updated yet.
        const resultSet = typesenseEnabled ? typesenseResults : contacts;
        resultSet.forEach(contact => {
            if (Array.isArray(contact.contactFocus)) {
                contact.contactFocus.forEach(f => f && options.add(f));
            } else if (contact.contactFocus) {
                options.add(contact.contactFocus);
            }
        });
        return Array.from(options).sort();
    }, [contactFocusCollectionOptions, typesenseEnabled, typesenseResults, contacts]);

    const handleSort = (field) => {
        const newDir = sortBy === field ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
        setTableSort(field, newDir);
    };

    const sortArrow = (field) => {
        if (sortBy !== field) return '';
        return sortDir === 'asc' ? ' ▲' : ' ▼';
    };

    const handleContactClick = (contact) => {
        // Navigate to contact detail page using the contact's document ID
        // Pass state to indicate user came from Contacts page
        navigate(`/customer/${contact.customerId}/contact/${contact.id}`, {
            state: { fromContacts: true }
        });
    };

    if (loading) {
        return (
            <main className="flex-grow p-6 overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Contacts</h2>
                </div>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="flex items-center justify-center py-4 gap-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-500 text-sm">Loading contacts...</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-xs font-semibold text-gray-500 uppercase border-b bg-gray-50">
                                    <th className="px-4 py-3 text-left">Name</th>
                                    <th className="px-4 py-3 text-left">Customer</th>
                                    <th className="px-4 py-3 text-left">Email</th>
                                    <th className="px-4 py-3 text-left">Phone</th>
                                    <th className="px-4 py-3 text-left">Title</th>
                                    <th className="px-4 py-3 text-left">Focus</th>
                                    <th className="px-4 py-3 text-left">Sales Rep</th>
                                    <th className="px-4 py-3 text-left">Is Primary?</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y">
                                {[...Array(10)].map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
                                        <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                                        <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-40"></div></td>
                                        <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                                        <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                                        <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                                        <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                                        <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="flex-grow p-6 overflow-y-auto">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}

            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Contacts</h2>
            </div>

            {/* Search and Filters */}
            <div className="mb-4 space-y-3">
                {/* Search */}
                <div className="flex items-center">
                    <Tooltip text="Search contacts by name, email, phone, position, customer, or sales rep">
                        <input
                            type="text"
                            placeholder="Search contacts..."
                            className={`border rounded px-3 py-2 w-full md:w-[24rem] transition-colors duration-150 ${
                                fb.draft.q ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                            }`}
                            value={fb.draft.q}
                            onChange={e => fb.setDraft('q', e.target.value)}
                            onKeyDown={fb.applyOnEnter}
                        />
                    </Tooltip>
                </div>
                
                {/* Filters */}
                <div className="flex flex-wrap gap-3">
                        <select
                            value={fb.draft.rep}
                            onChange={e => fb.setDraft('rep', e.target.value)}
                            className="border border-gray-300 rounded px-3 py-2 text-sm"
                        >
                            <option value="">All Sales Reps</option>
                            {uniqueSalesReps.map(rep => (
                                <option key={rep} value={rep}>{rep}</option>
                            ))}
                        </select>
                    
                        <select
                            value={fb.draft.focus}
                            onChange={e => fb.setDraft('focus', e.target.value)}
                            className="border border-gray-300 rounded px-3 py-2 text-sm"
                        >
                            <option value="">All Focus Areas</option>
                            <option value="__EMPTY__">(Unassigned)</option>
                            {uniqueFocusOptions.map(focus => (
                                <option key={focus} value={focus}>{focus}</option>
                            ))}
                        </select>
                   
                        <select
                            value={fb.draft.primary}
                            onChange={e => fb.setDraft('primary', e.target.value)}
                            className="border border-gray-300 rounded px-3 py-2 text-sm"
                        >
                            <option value="all">Primary & Non-Primary</option>
                            <option value="primary">Primary Only</option>
                            <option value="non-primary">Non-Primary Only</option>
                        </select>
                    
                    <FilterActions onApply={fb.apply} onClear={fb.clear} isDirty={fb.isDirty} hasActiveFilters={fb.hasActiveFilters} />
                </div>
            </div>

            {/* Contacts Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="text-xs font-semibold text-gray-500 uppercase border-b bg-gray-50">
                                <th 
                                    className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('fullName')}
                                >
                                    Name{sortArrow('fullName')}
                                </th>
                                <th 
                                    className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('customerName')}
                                >
                                    Customer{sortArrow('customerName')}
                                </th>
                                <th 
                                    className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('email')}
                                >
                                    Email{sortArrow('email')}
                                </th>
                                <th 
                                    className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('phone')}
                                >
                                    Phone{sortArrow('phone')}
                                </th>
                                <th 
                                    className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('contactPosition')}
                                >
                                    Title{sortArrow('contactPosition')}
                                </th>
                                <th 
                                    className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('contactFocus')}
                                >
                                    Focus{sortArrow('contactFocus')}
                                </th>
                                <th 
                                    className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('salesReps')}
                                >
                                    Sales Rep{sortArrow('salesReps')}
                                </th>
                                <th 
                                    className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('isPrimary')}
                                >
                                    Is Primary?{sortArrow('isPrimary')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y">
                            {paginatedContacts.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                                        {fb.applied.q || fb.applied.rep || fb.applied.focus || fb.applied.primary !== 'all' 
                                            ? 'No contacts found matching your search/filters' 
                                            : 'No contacts available'}
                                    </td>
                                </tr>
                            ) : (
                                paginatedContacts.map(contact => {
                                    const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
                                    const focusDisplay = Array.isArray(contact.contactFocus) 
                                        ? contact.contactFocus.join(', ') 
                                        : (contact.contactFocus || '-');
                                    const salesRepsDisplay = contact.salesRepNames?.length > 0
                                        ? contact.salesRepNames.join(', ')
                                        : '-';

                                    return (
                                        <tr key={contact.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3">
                                                <button
                                                    onClick={() => handleContactClick(contact)}
                                                    className="text-indigo-600 hover:underline text-left font-medium"
                                                >
                                                    {fullName || '-'}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3">
                                                <button
                                                    onClick={() => navigate(`/customer/${contact.customerId}`)}
                                                    className="text-indigo-600 hover:underline text-left"
                                                >
                                                    {contact.customerName}
                                                </button>
                                                <div className="text-xs text-gray-500">
                                                    {contact.customerAccountNumber ? `ID: ${contact.customerAccountNumber}` : ''}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm">{contact.email || '-'}</td>
                                            <td className="px-4 py-3 text-sm">{contact.phone || '-'}</td>
                                            <td className="px-4 py-3 text-sm">{contact.contactPosition || '-'}</td>
                                            <td className="px-4 py-3 text-sm">{focusDisplay}</td>
                                            <td className="px-4 py-3 text-sm">{salesRepsDisplay}</td>
                                            <td className="px-4 py-3 text-sm">
                                                {contact.isPrimary ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                        Primary
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={typesenseEnabled ? typesenseTotalFound : sortedContacts.length} pageSize={PAGE_SIZE} itemLabel="contacts" className="mt-6" />
        </main>
    );
};

export default ContactsPage;



